import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatBRL } from "@/lib/api";
import { Check, ShieldCheck, Sparkles, Heart, Building2, Stethoscope, Pill, Eye, Brain, Activity, Star, ArrowRight } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1535722339661-7f22185bc7ca?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const NETWORK_IMG = "https://images.pexels.com/photos/7659873/pexels-photo-7659873.jpeg?auto=compress&cs=tinysrgb&w=1200";
const B2B_IMG = "https://images.pexels.com/photos/8123801/pexels-photo-8123801.jpeg?auto=compress&cs=tinysrgb&w=1200";

const PLAN_ACCENTS = {
  corporativo: { bg: "#0E2A5C", text: "white", badge: "B2B" },
  gold: { bg: "#F59E0B", text: "white", badge: "Mais popular" },
  diamante: { bg: "#0EA5E9", text: "white", badge: "Premium" },
};

const SPECIALTY_ICONS = {
  Cardiologia: Heart,
  Odontologia: Sparkles,
  "Exames Laboratoriais": Activity,
  Farmácia: Pill,
  Oftalmologia: Eye,
  Pediatria: ShieldCheck,
  Psicologia: Brain,
  Fisioterapia: Stethoscope,
};

export default function Landing() {
  const [plans, setPlans] = useState([]);
  const [partners, setPartners] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data));
    api.get("/partners").then(({ data }) => setPartners(data));
    api.get("/partners/categories").then(({ data }) => setCats(data));
  }, []);

  return (
    <div className="bg-white" data-testid="landing-page">
      {/* HERO */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        <div aria-hidden className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full" style={{ background: "radial-gradient(circle, rgba(42,143,224,0.15) 0%, transparent 70%)" }} />
        <div aria-hidden className="absolute -bottom-40 -left-40 w-[420px] h-[420px] rounded-full" style={{ background: "radial-gradient(circle, rgba(14,42,92,0.10) 0%, transparent 70%)" }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 text-[#2A8FE0] text-xs font-bold uppercase tracking-widest" data-testid="hero-badge">
              <Sparkles className="h-3.5 w-3.5" /> Descontos & Vantagens em todo Brasil
            </span>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight text-[#0E2A5C] text-balance" data-testid="hero-title">
              Saúde, bem-estar e <span className="text-[#2A8FE0]">economia</span> no mesmo cartão.
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-2xl text-balance" data-testid="hero-subtitle">
              Acesso a uma rede credenciada com clínicas, laboratórios, farmácias e especialistas — com descontos exclusivos para você, sua família ou sua empresa.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/cadastro" className="btn-primary" data-testid="hero-cta-cadastro">
                Solicitar meu cartão <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/cadastro?role=credenciado" className="btn-outline" data-testid="hero-cta-credenciado">
                Sou credenciado
              </Link>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
              {[["+8 mil", "credenciados"], ["+50%", "de desconto"], ["24/7", "telemedicina"]].map(([n, l]) => (
                <div key={l} className="border-l-2 border-[#2A8FE0]/40 pl-4">
                  <div className="font-display text-2xl font-bold text-[#0E2A5C]">{n}</div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">{l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative fade-up">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl">
              <img src={HERO_IMG} alt="Família feliz com Solimões Card" className="w-full h-full object-cover" data-testid="hero-image" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0E2A5C]/40 to-transparent" />
            </div>
            {/* floating wallet */}
            <div className="absolute -bottom-8 -left-8 w-72 wallet-card float-slow hidden md:block" data-testid="hero-wallet-card">
              <div className="flex items-start justify-between relative z-10">
                <div>
                  <div className="text-xs uppercase tracking-widest opacity-75">Solimões Card</div>
                  <div className="font-display text-xl font-black mt-1">Gold</div>
                </div>
                <div className="text-xs font-bold bg-white/20 px-2.5 py-1 rounded-full">DESCONTOS</div>
              </div>
              <div className="mt-12 font-mono text-base tracking-widest relative z-10">5612 •••• •••• 9032</div>
              <div className="mt-3 flex justify-between items-end relative z-10">
                <div className="text-[10px] uppercase tracking-widest opacity-80">Titular<br/><span className="text-sm font-bold normal-case tracking-normal opacity-100">Sua Família</span></div>
                <div className="text-[10px] uppercase tracking-widest opacity-80">Validade<br/><span className="text-sm font-bold normal-case tracking-normal opacity-100">12/30</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PLANS */}
      <section id="planos" className="section-pad bg-[#F8FAFC]" data-testid="plans-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-bold uppercase tracking-widest text-[#2A8FE0]">Escolha seu plano</span>
            <h2 className="mt-3 font-display text-4xl lg:text-5xl font-black text-[#0E2A5C]">Três caminhos para economizar</h2>
            <p className="mt-4 text-slate-600">Do cartão básico ao premium com crediário, e até soluções co-branded para empresas — você decide o nível de cuidado.</p>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {plans.map((p) => {
              const accent = PLAN_ACCENTS[p.id];
              const featured = p.id === "gold";
              return (
                <div
                  key={p.id}
                  className={`card-soft p-8 relative ${featured ? "ring-2 ring-[#2A8FE0] md:scale-[1.04]" : ""}`}
                  data-testid={`plan-card-${p.id}`}
                >
                  <div className="absolute -top-3 left-8 px-3 py-1 rounded-full text-[10px] uppercase font-bold tracking-widest text-white" style={{ background: accent.bg }}>
                    {accent.badge}
                  </div>
                  <div className="font-display text-2xl font-black text-[#0E2A5C]">{p.name}</div>
                  <div className="text-sm text-slate-500 mt-1">{p.audience}</div>
                  <div className="mt-6 flex items-baseline gap-1">
                    <span className="font-display text-5xl font-black text-[#0E2A5C]">{formatBRL(p.price).replace("R$", "R$ ")}</span>
                    <span className="text-sm text-slate-500">/mês</span>
                  </div>
                  <p className="mt-3 text-sm text-slate-600">{p.tagline}</p>
                  <ul className="mt-6 space-y-3">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="h-4 w-4 mt-0.5 text-[#2A8FE0] flex-none" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={`/cadastro?plan=${p.id}`} className={`mt-8 ${featured ? "btn-primary" : "btn-outline"} w-full`} data-testid={`plan-cta-${p.id}`}>
                    Quero este plano
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* NETWORK */}
      <section id="rede" className="section-pad" data-testid="network-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-28">
            <span className="text-xs font-bold uppercase tracking-widest text-[#2A8FE0]">Rede Credenciada</span>
            <h2 className="mt-3 font-display text-4xl lg:text-5xl font-black text-[#0E2A5C] text-balance">Mais de 8 mil parceiros prontos para te atender</h2>
            <p className="mt-4 text-slate-600">Encontre clínicas, laboratórios, farmácias e especialistas perto de você. Atendimento com descontos imediatos na hora do pagamento.</p>
            <div className="mt-6 rounded-3xl overflow-hidden">
              <img src={NETWORK_IMG} alt="Profissional de saúde" className="w-full h-72 object-cover" />
            </div>
            <Link to="/rede" className="btn-secondary mt-6" data-testid="network-cta">Ver toda a rede</Link>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {(cats.length ? cats : ["Cardiologia","Odontologia","Exames Laboratoriais","Farmácia","Oftalmologia","Pediatria","Psicologia","Fisioterapia"]).map((c) => {
              const Icon = SPECIALTY_ICONS[c] || Stethoscope;
              const count = partners.filter(p => p.category === c).length;
              return (
                <div key={c} className="card-soft p-6" data-testid={`category-${c}`}>
                  <div className="h-11 w-11 rounded-xl bg-blue-50 text-[#2A8FE0] flex items-center justify-center"><Icon className="h-5 w-5" /></div>
                  <div className="mt-4 font-display text-lg font-bold text-[#0E2A5C]">{c}</div>
                  <div className="text-sm text-slate-500 mt-1">{count} parceiro(s) próximos</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* B2B */}
      <section className="section-pad bg-[#0E2A5C] relative overflow-hidden" data-testid="b2b-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 text-white">
            <span className="text-xs font-bold uppercase tracking-widest text-[#7BB7E8]">Solimões Card Corporativo</span>
            <h2 className="mt-3 font-display text-4xl lg:text-5xl font-black text-balance">Cuide do seu time com um cartão co-branded</h2>
            <p className="mt-4 text-white/80 max-w-xl">Logomarca da sua empresa + Solimões. Faturamento consolidado por CNPJ, gestão completa de vidas e relatórios gerenciais.</p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3 text-sm text-white/90">
              {["Gestão de vidas por CNPJ", "Faturamento consolidado", "Co-branding na arte", "Relatórios mensais"].map(x => (
                <li key={x} className="flex items-center gap-2"><Building2 className="h-4 w-4 text-[#7BB7E8]" />{x}</li>
              ))}
            </ul>
            <Link to="/cadastro?plan=corporativo" className="btn-primary mt-8" data-testid="b2b-cta">Falar com especialista</Link>
          </div>
          <div className="lg:col-span-6">
            <img src={B2B_IMG} alt="Reunião empresarial" className="rounded-3xl shadow-2xl" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad bg-[#F8FAFC]" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-xs font-bold uppercase tracking-widest text-[#2A8FE0]">Depoimentos</span>
            <h2 className="mt-3 font-display text-4xl lg:text-5xl font-black text-[#0E2A5C]">Quem usa, recomenda</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {[
              { n: "Mariana R.", t: "Família Gold", q: "Já economizei mais de R$ 1.200 só nas consultas pediátricas dos meus filhos." },
              { n: "Carlos M.", t: "Plano Diamante", q: "O crediário na rede premium foi um divisor de águas para o tratamento ortodôntico." },
              { n: "RH — Indústria Norte", t: "Corporativo", q: "Implantamos para 230 colaboradores. A gestão por CNPJ ficou simples e transparente." },
            ].map(t => (
              <div key={t.n} className="card-soft p-7" data-testid={`testimonial-${t.n}`}>
                <div className="flex gap-1 text-[#F59E0B]">{[...Array(5)].map((_,i)=><Star key={i} className="h-4 w-4 fill-current" />)}</div>
                <p className="mt-4 text-slate-700">"{t.q}"</p>
                <div className="mt-5 pt-5 border-t border-slate-100">
                  <div className="font-bold text-[#0E2A5C]">{t.n}</div>
                  <div className="text-xs text-slate-500">{t.t}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FOOTER */}
      <section className="section-pad" data-testid="footer-cta-section">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="rounded-3xl p-12 lg:p-16 text-center" style={{background: "linear-gradient(135deg, #0E2A5C 0%, #2A8FE0 100%)"}}>
            <h2 className="font-display text-3xl lg:text-5xl font-black text-white text-balance">Pronto para economizar com saúde?</h2>
            <p className="mt-4 text-white/85 max-w-2xl mx-auto">Solicite seu Solimões Card em minutos. Sem carência, sem burocracia.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/cadastro" className="btn-primary bg-white text-[#0E2A5C] hover:bg-slate-100" data-testid="footer-cta-cadastro" style={{boxShadow: "0 4px 14px 0 rgba(0,0,0,0.15)"}}>
                Solicitar agora
              </Link>
              <Link to="/entrar" className="btn-outline bg-transparent border-white text-white hover:border-white hover:text-white hover:bg-white/10" data-testid="footer-cta-entrar">
                Já sou cliente
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-100 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-4">
          <div className="text-sm text-slate-500">© {new Date().getFullYear()} Solimões Card — Descontos e Vantagens</div>
          <div className="text-sm text-slate-500">Manaus / AM • CNPJ exemplo • contato@solimoescard.com.br</div>
        </div>
      </footer>
    </div>
  );
}
